/**
 * Redux store configuration.
 *
 * Combines all feature slices under a single root reducer and mounts
 * `redux-saga` middleware to handle side-effect workflows.
 *
 * ### Logout behaviour
 * On `"LOGOUT"` the root reducer selectively resets state: `machineId`,
 * `chart` panel layout, and `alerts` are preserved; everything else resets.
 * `oneMinuteData` and `activeSymbols` are cleared so today-fill re-fetches.
 *
 * ### Platform notes
 * The `machineId` slice stores a device identifier on all platforms:
 * - Desktop: hardware UUID from Tauri
 * - Web: persistent random UUID from localStorage
 * - Mobile: Capacitor Device plugin identifier
 *
 * The `ipcEventsModal` slice controls desktop-only modals (exit confirmation,
 * auto-update). On web/mobile, these modals never trigger but the slice
 * remains to avoid conditional reducer juggling.
 *
 * ### Performance notes
 * `serializableCheck` and `immutableCheck` are disabled because the chart
 * state may hold 25,000+ `OneMinuteBar` objects per symbol — enabling these
 * checks causes deep-clone traversal on every dispatch.
 */
import { configureStore, combineReducers } from "@reduxjs/toolkit";
import createSagaMiddleware from "redux-saga";

import authSlice from "./auth-slice";
import machineIdSlice from "./machine-id-slice";
import metadataSlice from "./metadata-slice";
import backfillSlice from "./backfill-slice";
import watchlistSlice from "./watchlist-slice";
import { rootSaga } from "../middleware";
import themeSlice from "./theme-slice";
import ipcEventsModalSlice from "./ipc-events-modal-splice";
import chartSlice from "./chart-slice";
import alertSlice from "./alert-slice";
import connectionStatusSlice from "./connection-status-slice";
import amibrokerSlice from "./amibroker-slice";

const sagaMiddleware = createSagaMiddleware();

const combinedReducer = combineReducers({
  machineId: machineIdSlice,
  auth: authSlice,
  metadata: metadataSlice,
  backfill: backfillSlice,
  watchlist: watchlistSlice,
  theme: themeSlice,
  ipcEventsModal: ipcEventsModalSlice,
  chart: chartSlice,
  alerts: alertSlice,
  connectionStatus: connectionStatusSlice,
  amibroker: amibrokerSlice,
});

const rootReducer = (state, action) => {
  if (action.type === "LOGOUT") {
    const { machineId, chart, alerts } = state;
    // Preserve machineId, chart layout, and alerts — reset everything else.
    // Clear oneMinuteData + eodData so that on re-login todayfill/backfill
    // re-fetches fresh data. Clear historyLoadingSymbols + pendingTickBars
    // so the loading gate doesn't persist from a stale session.
    state = {
      machineId,
      chart: {
        ...chart,
        oneMinuteData: {},
        eodData: {},
        activeSymbols: [],
        historyLoadingSymbols: [],
        pendingTickBars: {},
      },
      alerts,
      // Force a clean AmiBroker lifecycle for the next login/session.
      amibroker: { isEnabled: false, isInitialized: false },
    };
  }
  return combinedReducer(state, action);
};
export const store = configureStore({
  reducer: rootReducer,
  middleware(getDefaultMiddleware) {
    return getDefaultMiddleware({
      thunk: false,
      // Disable expensive deep-equality checks on large chart data arrays.
      // With 25K+ OneMinuteBar objects per symbol, these checks clone/inspect
      // the entire state tree on every dispatch, causing massive GC pressure.
      serializableCheck: false,
      immutableCheck: false,
    }).concat(sagaMiddleware);
  },
});

sagaMiddleware.run(rootSaga);
